import ReportDesigner from '@components/Customization/ReportDesigner/ReportDesigner';
import CopyDesignDialog from '@components/Customization/ReportDesigns/CopyDesignDialog';
import { useDesignOwners } from '@components/Customization/ReportDesigns/useDesignOwners';
import Header from '@components/Shared/Layout/Header';
import { ROUTE_PATHS } from '@constants/routes';
import { designWrites } from '@lib/customization/reportDesign';
import { keptDraftsOf } from '@lib/customization/unsavedWork';
import { useWritesLeftBehind } from '@lib/customization/useWriteChain';
import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { backButtonStyles } from '@pages/CustomPoiTypes.styles';
import { detailBarStyles, detailOwnerStyles } from '@pages/ReportDesigns.styles';
import {
  ownerLabel, useAddReportDesignMutation, useGetReportDesignQuery, type CreateReportDesignInput,
} from '@store/api/reportDesignEndpoints';
import { isServerError } from '@store/error';
import { usePermission } from '@store/hooks';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { AngleLeftIcon, BuildingIcon, UserFriendsIcon } from 'react-line-awesome';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

const FORBIDDEN = 403;
const NOT_FOUND = 404;

/**
 * What the server answers for a design that is not the reader's to open, each with the two
 * lines that say it: one for a page with nothing to show, and one for the editor that still
 * holds the design as an earlier read brought it.
 */
const refusals: Record<number, { page: string; editor: string } | undefined> = {
  [FORBIDDEN]: { page: 'ReportDesign_NoAccess', editor: 'ReportDesign_Editor_AccessLost' },
  [NOT_FOUND]: { page: 'ReportDesign_NotFound', editor: 'ReportDesign_Editor_Deleted' },
};

interface SettledProps extends ComponentProps<typeof ReportDesigner> {
  /** The design is being read again. */
  fetching: boolean;
}

/**
 * The designer, once nothing an earlier visit sent is still on its way. Someone who leaves
 * during a save or an upload and is back before it has ended would otherwise take up the
 * draft that write is about to change, and be told their own save was somebody else's.
 * Mounted afresh for each design, as the designer is.
 *
 * A read of the design that is on its way by then is waited for as well: it is the one such
 * a save set off, and the designer opens on what it brings. Only before the designer is
 * first shown, which from then on knows itself what its own saves have made of the design.
 */
const SettledDesigner: React.FC<SettledProps> = ({ fetching, ...props }) => {
  const waiting = useWritesLeftBehind(designWrites(props.design._id));
  const opened = useRef(false);

  if (!waiting && !fetching) opened.current = true;

  return opened.current ? <ReportDesigner {...props} /> : <LinearProgress sx={{ width: '100%' }} />;
};

/**
 * One report design in the editor.
 *
 * The list stays a list and this page owns the design, the same split the POI type pages
 * use. It reads the one design, not the list: the list carries no content. A reader who may
 * not change the design gets the same page with every control disabled, the stored design in
 * the preview, and a way to copy it to where they may.
 *
 * A design that is deleted, or taken from the reader, while it is open here stays on screen
 * the same way, under a line that says what became of it: what was typed and not saved can
 * still be read there, and nothing more is sent.
 */
const ReportDesignDetailPage = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { userId } = usePermission();
  const { designId } = useParams<{ designId: string }>();

  // `currentData`, not `data`: going from one design to another keeps this page mounted, and
  // `data` is still the design that was left until the other one arrives.
  const { currentData: design, isFetching, error, refetch } = useGetReportDesignQuery(
    designId ?? '', { skip: !designId },
  );

  // Only what the server said is said here. Anything else (a server that cannot be reached,
  // one that is refusing everybody for the moment) is not the design's doing, and "not
  // found" would send its author looking for a design that is still there.
  const failure = { error };
  const refusal = refusals[isServerError(failure) ? failure.error.status : 0];

  // A read that is refused leaves the copy an earlier one brought, so a design that was
  // deleted or taken from the reader while it was open is still here to show. It is not
  // theirs to change any more, whatever that copy says they may do.
  const canEdit = !refusal && has(design, 'report_design:put');

  // Where a copy could go matters only to a reader who cannot change the design itself, and
  // not at all once there is no design left to copy.
  const { owners, ready: ownersReady } = useDesignOwners({ skip: !design || canEdit || Boolean(refusal) });
  const [addReportDesign, { isLoading: isCopying }] = useAddReportDesignMutation();
  const [copying, setCopying] = useState(false);

  // What the reader left unsaved of such a design has nowhere to be saved to. It is not
  // kept: a reload or a close of the tab would go on asking about it, with no editor left
  // to give it up in.
  useEffect(() => {
    if (refusal && designId) keptDraftsOf('report-design').drop(userId, designId);
  }, [refusal, userId, designId]);

  const backToList = () => navigate(ROUTE_PATHS.CUSTOMIZATIONS_REPORT_DESIGNS);

  const back = (
    <Button onClick={backToList} startIcon={<AngleLeftIcon />} color="inherit" sx={{ ...backButtonStyles, mb: 0 }}>
      {t('ReportDesign_Back')}
    </Button>
  );

  if (!design) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        {isFetching
          ? <LinearProgress sx={{ width: '100%' }} />
          : (
            <Box sx={{ width: '100%' }}>
              <Stack direction="row" sx={detailBarStyles}>{back}</Stack>
              <Typography color="text.secondary">{t(refusal?.page ?? 'ReportDesign_LoadFailed')}</Typography>
              {!refusal && designId && (
                <Button size="small" variant="outlined" color="inherit" sx={{ mt: 1.5 }} onClick={() => { void refetch(); }}>
                  {t('ReportPreview_Retry')}
                </Button>
              )}
            </Box>
          )}
      </Box>
    );
  }

  /** The copy is what the reader wanted to work on, so it opens in place of the original. */
  const handleCopy = async (input: CreateReportDesignInput) => {
    const result = await addReportDesign(input);

    if (result.error || !result.data) return;

    setCopying(false);
    navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_REPORT_DESIGN, { designId: result.data.id }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Box sx={{ width: '100%' }}>
        <Stack direction="row" sx={detailBarStyles}>
          {back}
          <Stack direction="row" sx={detailOwnerStyles}>
            {design.teamId === null ? <BuildingIcon /> : <UserFriendsIcon />}
            <Typography variant="body2" noWrap>{ownerLabel(design)}</Typography>
          </Stack>
        </Stack>

        <SettledDesigner
          // A different design starts a fresh draft.
          key={design._id}
          design={design}
          canEdit={canEdit}
          reread={refetch}
          fetching={isFetching}
          // The design the reader had open, which the server has since answered is gone or not
          // theirs: the designer stays, for what it holds to be read, and says this over it.
          closed={refusal ? t(refusal.editor) : undefined}
          // Only once the places a copy can go are known: before that the list could be the
          // organization alone, and the copy of a team's design would land there.
          onCopy={!refusal && !canEdit && ownersReady && owners.length > 0 ? () => setCopying(true) : undefined}
        />
      </Box>

      <CopyDesignDialog
        design={copying ? design : null}
        owners={owners}
        saving={isCopying}
        onClose={() => setCopying(false)}
        onCreate={handleCopy}
      />
    </Box>
  );
};

export default ReportDesignDetailPage;
